import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { ERouteLinks } from 'models/route';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'hooks/useQuery';
import { useTranslations } from 'hooks/useTranslations';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { formatDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';

interface ILegalEntityPreviousNamesSurnamesTableCard {
  data: ITableDataSource;
}

const LegalEntityPreviousNamesSurnamesTableCard = ({
  data,
}: ILegalEntityPreviousNamesSurnamesTableCard) => {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;
  const { hasPermissions } = usePermission();
  const { shortDateFormat } = useGlobalProperty();

  const renderDate = (date: string) => formatDate(date, shortDateFormat);

  const { t } = useTranslations();
  return (
    <Grid
      sx={{
        backgroundColor: colors.gray100,
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        padding: '20px',
        maxWidth: 'calc(100vw - 48px)',
        rowGap: 3,
      }}
    >
      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.previousNamesSurnamesTab.firstName.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.firstName)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.previousNamesSurnamesTab.middleName.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.middleNames)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.previousNamesSurnamesTab.surname.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.lastName)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.previousNamesSurnamesTab.createDate.column')}
        </Typography>
        <Typography variant="body2">{renderDate(data.createdDate)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.previousNamesSurnamesTab.addedBy.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.createdByName)}</Typography>
      </Grid>

      {hasPermissions([EPermission.MANAGE_LE, EPermission.DELETE_LE], true) && (
        <Grid
          container
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: {
              xs: `1px solid ${colors.primary100}`,
              lg: 'none',
            },
            mt: {
              xs: 0,
            },
          }}
          spacing={2}
        >
          <Grid size={{ xs: 12, sm: 6 }} sx={{ mb: { xs: 2, sm: 0 } }}>
            <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
              {t('legalEntityDetails.previousNamesSurnamesTab.action.column')}:{' '}
            </Typography>
          </Grid>

          {hasPermissions([EPermission.MANAGE_LE]) && (
            <Grid size={{ xs: 12, sm: 3 }}>
              <Button
                label={t('legalEntityDetails.previousNamesSurnamesTab.edit.link')}
                variant={EButtonVariants.outlined}
                onClick={() =>
                  navigate(
                    `${ERouteLinks.LegalEntityEditPreviousNamesSurnames.replace(
                      ':id',
                      id as string,
                    ).replace(
                      ':previousNamesSurnamesId',
                      data.id as string,
                    )}?type=${type}&expand=previousNamesSurnames`,
                  )
                }
                permission={[EPermission.MANAGE_LE]}
                size={EButtonSizes.small}
                fullWidth
              />
            </Grid>
          )}
          {hasPermissions([EPermission.MANAGE_LE]) && (
            <Grid size={{ xs: 12, sm: 3 }}>
              <Button
                label={t('legalEntityDetails.previousNamesSurnamesTab.delete.link')}
                variant={EButtonVariants.outlined}
                onClick={() =>
                  navigate(
                    `${ERouteLinks.LegalEntityDeletePreviousNamesSurnames.replace(
                      ':id',
                      id as string,
                    ).replace(
                      ':previousNamesSurnamesId',
                      data.id as string,
                    )}?type=${type}&expand=previousNamesSurnames`,
                  )
                }
                size={EButtonSizes.small}
                fullWidth
              />
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  );
};

export { LegalEntityPreviousNamesSurnamesTableCard };
